import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ_SECTIONS = [
  {
    name: "Purity & Certification",
    items: [
      ["What do 24K, 22K, 18K, 14K and 9KT mean?",
       "The karat says how much pure gold is in the alloy: 24K is 99.9%, 22K is 91.6%, 18K is 75%, 14K is 58.5% and 9KT is 37.5%. Siyaara's stock range is 9KT gold and 925 sterling silver — real, BIS-hallmarked gold that is harder-wearing for daily use and far more accessible. 14KT, 18KT and 22KT are crafted made-to-order with a 15–20 working day lead time. Every piece states its purity on the product page and on the invoice."],
      ["What is a BIS hallmark and HUID?",
       "BIS hallmarking is the Government of India's purity certification for gold jewellery, under standard IS 1417:2016. Siyaara gold jewellery carries the BIS hallmark with a six-character HUID code, which you can verify independently on the BIS website or the BIS Care app. Our registration — HM/C-7791471009 — covers gold jewellery and artefacts. Silver pieces are certified for their 999/925 purity instead; BIS gold hallmarking does not apply to them."],
      ["Are your coins certified?",
       "Yes. Every Siyaara coin is 999 fine, assay-certified, sealed in tamper-proof packaging and shipped with a Certificate of Authenticity carrying a unique serial and QR code. Scan it any time to verify the piece on our verification page."],
      ["Are diamonds and gemstones certified?",
       "Where a piece carries diamonds or gemstones, the stone details — type, weight and count — are listed in the product specifications, and certification details appear on your invoice."],
    ],
  },
  {
    name: "Pricing & Gold Rate",
    "items": [
      ["How are Siyaara prices calculated?",
       "Every price is computed live from today's metal rate: metal value (weight × the current per-gram rate for that purity), plus a transparent making or craft charge, plus 3% GST. On gold and coins you can open the full breakup on the product page and see each line."],
      ["Are prices inclusive of GST?",
       "Yes. Every price you see on Siyaara is inclusive of GST at 3%. Nothing is added at checkout beyond shipping choices you make yourself."],
      ["Why did the price change between visits?",
       "Because the metal rate moves. We price off the live rate rather than a padded static MRP, so prices move a little with the market — down as well as up. When you add a piece to your cart, its price is locked for a window (shown on a countdown) so you can check out calmly."],
      ["What is the cart price lock?",
       "The moment you add an item, we freeze its price for a fixed window — 30 minutes by default — against the current rate. If the window lapses, the cart refreshes to the current rate and tells you clearly what changed before you pay."],
      ["What sizes do your coins come in?",
       "Our handcrafted 999 fine coins start at 25 mg (0.025 g) and run through 50, 100, 200, 250, 300 and 500 mg up to 1 GM, 2.5 GM and 5 GM pieces, in 53 auspicious designs. Silver coins run from 500 mg upward."],
    ],
  },
  {
    name: "Orders & Delivery",
    items: [
      ["How long does delivery take?",
       "In-stock pieces ship in 4–6 working days. Made-to-order and personalised pieces take 7–10 working days to handcraft, then ship. Your estimated date appears on the product page when you enter your pincode."],
      ["Is shipping insured?",
       "Yes. Every order ships fully insured at our cost, in discreet tamper-evident packaging. High-value orders may require an OTP or ID check at delivery."],
      ["Can I track my order?",
       "Yes — from your account, or from the Track Order page with just your order number and phone number. You'll see each step: placed, packed, shipped, delivered."],
    ],
  },
  {
    name: "Returns & Exchange",
    items: [
      ["What is your return policy?",
       "Unworn jewellery in its original, untampered packaging can be returned within 2 days of delivery and exchanged within 10 days. Personalised and engraved pieces are made to order for you and are not returnable. Coins must be returned with the seal unbroken."],
      ["Do you offer exchange and buyback?",
       "Yes — lifetime exchange and buyback on all Siyaara pieces at the prevailing metal rate, with making charges adjusted per the policy in force. Bring or ship the piece with its certificate and invoice."],
      ["How do refunds work?",
       "Refunds go back to the original payment method within 5–7 working days after the returned piece passes quality check."],
    ],
  },
  {
    name: "Care & Resizing",
    items: [
      ["How do I care for my jewellery?",
       "Store pieces separately in the pouches provided, keep them away from perfumes and household chemicals, and wipe with a soft cloth after wear. Silver naturally tarnishes — a gentle silver-polish cloth restores it. We offer complimentary cleaning on Siyaara pieces."],
      ["Can my ring be resized?",
       "Most Siyaara rings can be resized up or down by one to two sizes. Eternity and engraved bands may not be resizable — the product page calls this out where relevant."],
    ],
  },
  {
    name: "Payments & EMI",
    items: [
      ["Which payment methods do you accept?",
       "UPI, all major credit and debit cards, and net banking. All orders are prepaid — we do not offer cash on delivery. Prepaid orders carry an extra discount where offered — it's applied automatically when you choose a prepaid method."],
      ["Is EMI available?",
       "Card EMI options are offered at checkout on eligible orders above our EMI threshold, through the payment gateway. Available tenures and monthly amounts are shown before you confirm."],
      ["Is it safe to pay online?",
       "Payments are processed by our PCI-DSS compliant payment partner over encrypted connections. We never see or store your card number."],
    ],
  },
  {
    name: "Custom & Bulk",
    items: [
      ["Can you make a custom design?",
       "Yes — share a reference or an idea through the custom enquiry form on any coin or product page and our design team will respond with sketches and a quote. Custom pieces are made to order."],
      ["Do you take bulk or corporate orders?",
       "Yes — coins and gift sets for weddings, corporates and events, with volume pricing on craft charges. Write to us with quantities and your date; rates are locked on confirmation."],
    ],
  },
];

export default function Faq() {
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "FAQs — Purity, Pricing, Delivery & Returns | Siyaara";
    const ld = {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQ_SECTIONS.flatMap((s) => s.items.map(([name, text]) => ({
        "@type": "Question", name, acceptedAnswer: { "@type": "Answer", text },
      }))),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify(ld);
    document.getElementById("faq-jsonld")?.remove();
    document.head.appendChild(script);
    return () => document.getElementById("faq-jsonld")?.remove();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return FAQ_SECTIONS;
    const ql = q.toLowerCase();
    return FAQ_SECTIONS.map((s) => ({
      ...s,
      items: s.items.filter(([question, answer]) => (question + answer).toLowerCase().includes(ql)),
    })).filter((s) => s.items.length);
  }, [q]);

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="faq-page">
      <p className="eyebrow !text-gold">HELP CENTRE</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Frequently asked questions</h1>
      <div className="relative mt-6 max-w-md">
        <Search size={15} className="absolute left-3 top-3 text-ink-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the FAQs…"
          className="h-10 w-full rounded-full border border-brand/20 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          data-testid="faq-search" />
      </div>
      {filtered.length === 0 && <p className="text-sm text-ink-2 mt-8" data-testid="faq-empty">No matching questions — try “gold rate”, “returns”, “coins”…</p>}
      {filtered.map((section) => (
        <section key={section.name} className="mt-10" data-testid={`faq-section-${section.name.toLowerCase().replace(/[\s&]+/g, "-")}`}>
          <h2 className="font-heading text-xl font-semibold">{section.name}</h2>
          <div className="hairline" />
          <Accordion type="single" collapsible>
            {section.items.map(([question, answer], i) => (
              <AccordionItem key={i} value={`${section.name}-${i}`} className="border-brand/10">
                <AccordionTrigger className="text-left text-sm font-medium" data-testid={`faq-q-${section.name.slice(0, 4)}-${i}`}>{question}</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-2 leading-relaxed">{answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}
    </div>
  );
}
